import React, { useState, useEffect } from 'react';
import { 
  X, Check, DollarSign, Clock, FileText, AlertTriangle, 
  Users, Zap, ArrowRight, Sparkles
} from 'lucide-react';
import { Button } from '../ui/button';

const TiredOfSoftwareSection = ({ openBooking }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  const problems = [
    { 
      icon: DollarSign, 
      problem: 'Per-user fees that grow with your team', 
      pain: 'Costs spiral as you hire more staff',
      solution: 'One-time build cost, own it forever'
    },
    { 
      icon: Clock, 
      problem: 'Weeks of audit preparation stress', 
      pain: 'Scrambling to find documentation',
      solution: 'Audit-ready reports in one click'
    },
    { 
      icon: FileText, 
      problem: 'Complex participant documentation', 
      pain: 'Multiple systems, duplicated data entry',
      solution: 'Single platform, unified data'
    },
    { 
      icon: AlertTriangle, 
      problem: 'Incident tracking gaps', 
      pain: 'Risk of non-compliance with NDIS Commission',
      solution: 'Real-time tracking with auto-escalation'
    },
    { 
      icon: Users, 
      problem: 'Staff compliance monitoring', 
      pain: 'Expired credentials going unnoticed',
      solution: 'Automated alerts before expiry'
    },
    { 
      icon: Zap, 
      problem: 'Rigid software limitations', 
      pain: 'Changing your workflow to fit the system',
      solution: 'Software built around YOUR workflow'
    }
  ];

  // Auto-rotate through problems
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % problems.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [problems.length]);

  return (
    <section className="py-24 bg-page-bg relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="max-w-4xl mx-auto text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-red-50 rounded-full border border-red-100 mb-6">
            <AlertTriangle className="text-red-500 mr-2 animate-pulse" size={18} />
            <span className="text-sm font-medium text-red-600">The Challenge NDIS Providers Face</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary-navy mb-6">
            Tired of Software That{' '}
            <span className="relative inline-block">
              <span className="text-red-500">Doesn't Fit?</span>
              <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 8" fill="none">
                <path d="M2 6 Q50 2 100 6 T198 6" stroke="#ef4444" strokeWidth="3" strokeLinecap="round" fill="none" strokeDasharray="4 4"/>
              </svg>
            </span>
          </h2>
          <p className="text-xl text-text-secondary">
            Most NDIS providers struggle with rigid, expensive software that forces you to change how you work
          </p>
        </div>

        {/* Interactive Problem Cards */}
        <div className="grid lg:grid-cols-3 gap-6 mb-12">
          {problems.map((item, index) => {
            const Icon = item.icon;
            const isActive = activeIndex === index;
            
            return (
              <div
                key={index}
                onClick={() => setActiveIndex(index)}
                className={`relative p-6 rounded-2xl cursor-pointer transition-all duration-500 ${
                  isActive 
                    ? 'bg-white shadow-2xl shadow-red-500/10 scale-105 border-2 border-red-200' 
                    : 'bg-surface border border-border-line hover:border-red-200 hover:shadow-lg'
                }`}
              >
                {/* Problem Side */}
                <div className={`transition-all duration-500 ${isActive ? 'opacity-0 absolute' : 'opacity-100'}`}>
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      isActive ? 'bg-red-100' : 'bg-red-50'
                    }`}>
                      <Icon className="text-red-500" size={24} />
                    </div>
                    <div>
                      <p className="text-text-primary font-semibold mb-1">{item.problem}</p>
                      <p className="text-text-secondary text-sm">{item.pain}</p>
                    </div>
                  </div>
                </div>

                {/* Solution Side (shown when active) */}
                <div className={`transition-all duration-500 ${isActive ? 'opacity-100' : 'opacity-0 absolute'}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-care-teal/10 flex items-center justify-center flex-shrink-0">
                      <Check className="text-care-teal" size={24} />
                    </div>
                    <div>
                      <p className="text-xs text-care-teal font-semibold uppercase tracking-wider mb-1">GetCare Solution</p>
                      <p className="text-text-primary font-semibold">{item.solution}</p>
                    </div>
                  </div>
                </div>

                {/* Progress Bar */}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100 rounded-b-2xl overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-red-500 to-care-teal animate-progress"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Bottom Message */}
        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 px-8 py-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl border border-red-100 shadow-lg">
            <div className="flex items-center gap-3">
              <X className="text-red-500" size={28} />
              <p className="text-lg text-text-primary font-semibold">
                Generic SaaS platforms were <span className="text-red-600">not built</span> for your unique NDIS workflow
              </p>
            </div>
            <Button 
              onClick={openBooking}
              className="bg-gradient-to-r from-tech-blue to-care-teal hover:from-tech-blue/90 hover:to-care-teal/90 text-white px-6 py-3 whitespace-nowrap"
            >
              <Sparkles className="mr-2" size={18} />
              See the Difference
            </Button>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes progress {
          from { width: 0%; }
          to { width: 100%; }
        }
        .animate-progress {
          animation: progress 4s linear;
        }
      `}</style>
    </section>
  );
};

export default TiredOfSoftwareSection;
